import type { Vec3 } from '../contracts/game'
import type { TrackChunkDefinition } from './ChunkTypes'

export interface TrackFrameSample {
  readonly center: Vec3
  readonly forward: Vec3
  readonly right: Vec3
  readonly up: Vec3
}

function normalize(value: Readonly<Vec3>): Vec3 {
  const length = Math.hypot(value.x, value.y, value.z)
  return { x: value.x / length, y: value.y / length, z: value.z / length }
}

function cross(a: Readonly<Vec3>, b: Readonly<Vec3>): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  }
}

function curveOffset(amplitude: number, t: number): number {
  const oneMinus = 1 - t
  return amplitude * 16 * t * t * oneMinus * oneMinus
}

function curveDerivative(amplitude: number, t: number, length: number): number {
  return amplitude * 32 * t * (1 - t) * (1 - 2 * t) / length
}

function surfaceProfile(chunk: TrackChunkDefinition, localDistance: number): { height: number; slope: number } {
  let height = chunk.elevation + Math.tan(chunk.pitch) * localDistance
  let slope = Math.tan(chunk.pitch)
  if (chunk.archetype === 'wave') {
    const phase = localDistance / chunk.length * Math.PI * 2
    height += Math.sin(phase) * 2.2
    slope += Math.cos(phase) * 2.2 * Math.PI * 2 / chunk.length
  } else if (chunk.archetype === 'ramp' && chunk.gapStart !== null && localDistance < chunk.gapStart) {
    const rampSlope = 3.2 / chunk.gapStart
    height += localDistance * rampSlope
    slope += rampSlope
  }
  return { height, slope }
}

export function sampleChunkFrame(chunk: TrackChunkDefinition, localDistance: number): TrackFrameSample {
  const distance = Math.max(0, Math.min(chunk.length, localDistance))
  const t = chunk.length <= 0 ? 0 : distance / chunk.length
  const curve = chunk.curve ?? 0
  const profile = surfaceProfile(chunk, distance)
  const forward = normalize({
    x: curveDerivative(curve, t, Math.max(0.001, chunk.length)),
    y: profile.slope,
    z: 1,
  })
  const unbankedRight = normalize(cross({ x: 0, y: 1, z: 0 }, forward))
  const unbankedUp = normalize(cross(forward, unbankedRight))
  const cosine = Math.cos(chunk.bank)
  const sine = Math.sin(chunk.bank)
  const right = {
    x: unbankedRight.x * cosine + unbankedUp.x * sine,
    y: unbankedRight.y * cosine + unbankedUp.y * sine,
    z: unbankedRight.z * cosine + unbankedUp.z * sine,
  }
  const up = {
    x: unbankedUp.x * cosine - unbankedRight.x * sine,
    y: unbankedUp.y * cosine - unbankedRight.y * sine,
    z: unbankedUp.z * cosine - unbankedRight.z * sine,
  }
  return {
    center: {
      x: curveOffset(curve, t),
      y: profile.height,
      z: chunk.startZ + distance,
    },
    forward,
    right,
    up,
  }
}

export function positionOnTrack(chunk: TrackChunkDefinition, localDistance: number, lateral: number, vertical = 0): Vec3 {
  const frame = sampleChunkFrame(chunk, localDistance)
  return {
    x: frame.center.x + frame.right.x * lateral + frame.up.x * vertical,
    y: frame.center.y + frame.right.y * lateral + frame.up.y * vertical,
    z: frame.center.z + frame.right.z * lateral + frame.up.z * vertical,
  }
}
